/**
 * src/contexts/AuthContext.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Firebase Auth context — TypeScript version.
 * All Firebase initialization lives in src/firebase.js.
 * Import { auth } from there; never call initializeApp here.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import type { User } from 'firebase/auth';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  GoogleAuthProvider,
  GithubAuthProvider,
  signInWithPopup,
  updateProfile,
} from 'firebase/auth';
import { auth } from '../firebase';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

export interface AuthContextValue {
  currentUser:     User | null;
  loading:         boolean;
  error:           string | null;
  signUp:          (email: string, password: string, displayName?: string) => Promise<User>;
  signIn:          (email: string, password: string) => Promise<User>;
  signInWithGoogle: () => Promise<User>;
  signInWithGithub: () => Promise<User>;
  signOut:         () => Promise<void>;
  resetPassword:   (email: string) => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// CONTEXT
// ─────────────────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

const IS_PROD = window.location.hostname !== 'localhost'
  && window.location.hostname !== '127.0.0.1';

// ─────────────────────────────────────────────────────────────────────────────
// PROVIDER
// ─────────────────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading,     setLoading]     = useState<boolean>(true);
  const [error,       setError]       = useState<string | null>(null);

  // ── Sign Up ────────────────────────────────────────────────────────────────
  const signUp = async (email: string, password: string, displayName?: string): Promise<User> => {
    try {
      setError(null);
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      if (displayName) await updateProfile(cred.user, { displayName });
      if (IS_PROD && (window as { gtag?: (...a: unknown[]) => void }).gtag)
        (window as { gtag?: (...a: unknown[]) => void }).gtag!('event', 'sign_up', { method: 'email' });
      return cred.user;
    } catch (err) {
      const msg = (err as Error).message;
      setError(msg);
      throw err;
    }
  };

  // ── Sign In (email) ────────────────────────────────────────────────────────
  const signIn = async (email: string, password: string): Promise<User> => {
    try {
      setError(null);
      const cred = await signInWithEmailAndPassword(auth, email, password);
      if (IS_PROD && (window as { gtag?: (...a: unknown[]) => void }).gtag)
        (window as { gtag?: (...a: unknown[]) => void }).gtag!('event', 'login', { method: 'email' });
      return cred.user;
    } catch (err) {
      const msg = (err as Error).message;
      setError(msg);
      throw err;
    }
  };

  // ── Sign In (Google) ───────────────────────────────────────────────────────
  const signInWithGoogle = async (): Promise<User> => {
    try {
      setError(null);
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      const user   = result.user;
      const isNew  = user.metadata.creationTime === user.metadata.lastSignInTime
        || Math.abs(
             new Date(user.metadata.creationTime!).getTime() -
             new Date(user.metadata.lastSignInTime!).getTime()
           ) < 10000;
      if (IS_PROD && (window as { gtag?: (...a: unknown[]) => void }).gtag)
        (window as { gtag?: (...a: unknown[]) => void }).gtag!(
          'event', isNew ? 'sign_up' : 'login', { method: 'google' }
        );
      return user;
    } catch (err) {
      const msg = (err as Error).message;
      setError(msg);
      throw err;
    }
  };

  // ── Sign In (GitHub) ───────────────────────────────────────────────────────
  const signInWithGithub = async (): Promise<User> => {
    try {
      setError(null);
      const result = await signInWithPopup(auth, new GithubAuthProvider());
      const user   = result.user;
      const isNew  = user.metadata.creationTime === user.metadata.lastSignInTime
        || Math.abs(
             new Date(user.metadata.creationTime!).getTime() -
             new Date(user.metadata.lastSignInTime!).getTime()
           ) < 10000;
      if (IS_PROD && (window as { gtag?: (...a: unknown[]) => void }).gtag)
        (window as { gtag?: (...a: unknown[]) => void }).gtag!(
          'event', isNew ? 'sign_up' : 'login', { method: 'github' }
        );
      return user;
    } catch (err) {
      const msg = (err as Error).message;
      setError(msg);
      throw err;
    }
  };

  // ── Sign Out ───────────────────────────────────────────────────────────────
  const signOut = async (): Promise<void> => {
    try {
      await firebaseSignOut(auth);
      if (IS_PROD && (window as { gtag?: (...a: unknown[]) => void }).gtag)
        (window as { gtag?: (...a: unknown[]) => void }).gtag!('event', 'logout');
    } catch (err) {
      const msg = (err as Error).message;
      setError(msg);
      throw err;
    }
  };

  // ── Reset Password ─────────────────────────────────────────────────────────
  const resetPassword = async (email: string): Promise<void> => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
      if (IS_PROD && (window as { gtag?: (...a: unknown[]) => void }).gtag)
        (window as { gtag?: (...a: unknown[]) => void }).gtag!(
          'event', 'password_reset', { method: 'email' }
        );
    } catch (err) {
      const msg = (err as Error).message;
      setError(msg);
      throw err;
    }
  };

  // ── Auth state listener ────────────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value: AuthContextValue = {
    currentUser,
    loading,
    error,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithGithub,
    signOut,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HOOK
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the current auth context.
 * Throws if called outside <AuthProvider> — prevents silent null access.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an <AuthProvider>');
  return ctx;
}
